import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import Modal from 'react-bootstrap/Modal';
import ChatApp from './ChatApp';
import AddToWishlist from "./AddToWishlist";
import AddtoExclusion from "./AddtoExclusion";
import axios from "axios";
import { extractChatMessageText, parseJsonReply } from "../utils/openai";
import { getStatus, normaliseBooksResponse, timeAgo, CoverArt, STATUS_LABELS } from "../utils/books"; // eslint-disable-line no-unused-vars
import Icon from "./Icons";

const LocalApiUrl = "/api";
const GPT_MODEL   = "gpt-5.4";

async function openaiChat(body) {
  const res = await axios.post(`${LocalApiUrl}/openai`, body);
  return res.data;
}

function pickRandom(arr, n) {
  return [...arr].sort(() => Math.random() - 0.5).slice(0, n);
}

async function fetchCover(title, author) {
  try {
    const params = new URLSearchParams({ title, limit: 1, fields: 'cover_i' });
    if (author) params.set('author', author);
    const res = await axios.get(`https://openlibrary.org/search.json?${params}`);
    const coverId = res.data?.docs?.[0]?.cover_i;
    return coverId ? `https://covers.openlibrary.org/b/id/${coverId}-M.jpg` : null;
  } catch { return null; }
}

// ── CurrentlyReadingCard ──────────────────────────────────────────────────────
function CurrentlyReadingCard({ book, onUpdate, onFinish, onAbandon, onChat }) {
  const [editing, setEditing]   = useState(false);
  const [pct,     setPct]       = useState(book.progress || 0);
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = () => { onUpdate(book, pct); setEditing(false); };
  const daysSinceStart = book.startDate
    ? Math.floor((Date.now() - new Date(book.startDate)) / 86400000)
    : null;

  return (
    <div className="group bg-ink-surface/90 border border-ink-border rounded-2xl overflow-hidden flex flex-col shadow-card transition-all duration-300 hover:-translate-y-1 hover:border-ink-muted hover:shadow-lift">
      {/* Cover */}
      <div className="relative w-full aspect-[2/3] overflow-hidden flex-shrink-0 cursor-pointer"
           onClick={() => onChat(book)}>
        <CoverArt book={book} className="w-full h-full transition-transform duration-700 group-hover:scale-[1.04]" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-base/80 via-transparent to-transparent" />
        <div className="absolute bottom-2 left-3 right-3">
          <div className="h-1 bg-ink-muted rounded-full overflow-hidden">
            <div className="h-full bg-gold rounded-full transition-all duration-500"
                 style={{ width: `${pct}%` }} />
          </div>
          <p className="text-[10px] text-cream-muted mt-1">{pct}%</p>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col gap-1 flex-1">
        <p className="text-cream text-sm font-semibold leading-snug line-clamp-2">{book.title}</p>
        <p className="text-cream-muted text-[11px]">{book.author}</p>
        {daysSinceStart !== null && (
          <p className="text-cream-muted text-[10px]">
            {daysSinceStart === 0 ? 'Started today' : `${daysSinceStart}d in`}
          </p>
        )}

        {/* Progress update */}
        {editing ? (
          <div className="mt-2 flex items-center gap-1.5">
            <input ref={inputRef} type="number" min="0" max="100" value={pct}
              onChange={e => setPct(Math.min(100, Math.max(0, +e.target.value)))}
              className="w-14 bg-ink-raised border border-ink-border text-cream text-xs rounded px-2 py-1 text-center"
              onKeyDown={e => e.key === 'Enter' && save()}
            />
            <span className="text-cream-muted text-[10px]">%</span>
            <button onClick={save}
              className="text-[10px] bg-gold text-ink-base font-semibold px-2 py-1 rounded ml-auto">
              Save
            </button>
          </div>
        ) : (
          <div className="flex gap-1.5 mt-2 flex-wrap">
            <button onClick={() => setEditing(true)}
              className="text-[10px] text-cream-dim border border-ink-border px-2.5 py-1.5 rounded-lg hover:border-gold/40 hover:text-gold transition-colors">
              Update %
            </button>
            <button onClick={() => onFinish(book)}
              className="text-[10px] text-cream-dim border border-ink-border px-2.5 py-1.5 rounded-lg hover:border-ink-muted hover:text-cream transition-colors">
              Done
            </button>
            <button onClick={() => onAbandon(book)}
              className="text-[10px] text-cream-muted hover:text-crimson/70 transition-colors px-1">
              Abandon
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── RecentCard ────────────────────────────────────────────────────────────────
function RecentCard({ book, onChat }) {
  return (
    <div className="flex-shrink-0 w-24 cursor-pointer group" onClick={() => onChat(book)}>
      <div className="w-24 h-36 rounded-lg overflow-hidden border border-ink-border group-hover:border-ink-muted transition-colors mb-2">
        <CoverArt book={book} className="w-full h-full" />
      </div>
      <p className="text-cream text-[11px] font-medium leading-snug line-clamp-2">{book.title}</p>
      <p className="text-cream-muted text-[10px] mt-0.5">{timeAgo(book.finishDate || book.addedOn)}</p>
    </div>
  );
}

// ── FeaturedPick ──────────────────────────────────────────────────────────────
function FeaturedPick({ book, onAdd, onWishlist, onExclude, loading }) {
  if (loading) return (
    <div className="bg-ink-surface border border-ink-border rounded-xl p-5 animate-pulse">
      <div className="h-3 bg-ink-raised rounded w-1/5 mb-3" />
      <div className="flex gap-4">
        <div className="w-16 h-24 bg-ink-raised rounded-lg flex-shrink-0" />
        <div className="flex-1 space-y-2.5">
          <div className="h-4 bg-ink-raised rounded w-3/4" />
          <div className="h-3 bg-ink-raised rounded w-1/2" />
          <div className="h-3 bg-ink-raised rounded w-full" />
          <div className="h-3 bg-ink-raised rounded w-4/5" />
        </div>
      </div>
    </div>
  );
  if (!book) return null;
  return (
    <div className="bg-gradient-to-br from-ink-surface via-ink-surface to-[#17262d] border border-ink-border rounded-2xl overflow-hidden flex flex-col sm:flex-row shadow-card">
      <div className="sm:w-28 h-32 sm:h-auto flex-shrink-0">
        <CoverArt book={book} className="w-full h-full" />
      </div>
      <div className="flex-1 p-6 flex flex-col gap-2 min-w-0">
        {book.genre && <p className="text-gold text-[10px] font-semibold uppercase tracking-widest">{book.genre}</p>}
        <p className="font-serif text-cream text-2xl font-semibold leading-tight tracking-tight">{book.title}</p>
        <p className="text-cream-muted text-sm">{book.author}</p>
        {book.because_you_read && (
          <p className="text-cream-muted text-xs">
            Because you loved <span className="text-cream-dim font-medium">{book.because_you_read}</span>
          </p>
        )}
        {book.summary && <p className="text-cream-dim text-sm leading-relaxed line-clamp-2">{book.summary}</p>}
        <div className="flex items-center gap-2 mt-auto pt-1">
          <button onClick={() => onAdd(book)}     className="sc-btn-gold text-xs py-1.5 px-4">Add to library</button>
          <button onClick={() => onWishlist(book)} className="sc-btn-ghost text-xs py-1.5 px-4">Save for later</button>
          <button onClick={() => onExclude(book)} className="text-xs text-cream-muted hover:text-cream transition-colors px-2">Skip</button>
        </div>
      </div>
    </div>
  );
}

// ── AddForm ───────────────────────────────────────────────────────────────────
export function AddForm({ allBooks, onAdd, onCancel }) {
  const [title,      setTitle]      = useState("");
  const [addStatus,  setAddStatus]  = useState("finished");
  const [startDate,  setStartDate]  = useState("");
  const [finishDate, setFinishDate] = useState(new Date().toISOString().split('T')[0]);
  const [progress,   setProgress]   = useState(0);
  const [showDates,  setShowDates]  = useState(false);
  const [loading,    setLoading]    = useState(false);

  const duplicate = title.length > 2
    ? allBooks.find(b => b.title.toLowerCase().includes(title.toLowerCase()) ||
                         title.toLowerCase().includes(b.title.toLowerCase().slice(0, title.length)))
    : null;

  const statusOptions = [
    { key: 'finished',  label: 'Finished' },
    { key: 'reading',   label: 'Reading now' },
    { key: 'wishlist',  label: 'Want to read' },
    { key: 'abandoned', label: 'Abandoned' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setLoading(true);
    try { await onAdd({ title, status: addStatus, startDate, finishDate, progress }); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-ink-surface/95 border border-gold/20 rounded-2xl p-5 sm:p-6 space-y-5 shadow-lift animate-fade-in">
      {/* Title + duplicate warning */}
      <div>
        <label className="text-cream-muted text-xs font-medium block mb-1.5">Book title</label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="e.g. A Gentleman in Moscow"
          className="sc-input text-sm" required autoFocus />
        {duplicate && (
          <div className="mt-2 flex items-center gap-2 bg-gold/8 border border-gold/20 rounded-lg px-3 py-2">
            <div className="w-6 h-8 flex-shrink-0 rounded overflow-hidden">
              <CoverArt book={duplicate} className="w-full h-full" />
            </div>
            <p className="text-xs text-cream-dim">
              Already in your library: <span className="text-cream font-medium">{duplicate.title}</span>
              {' '}·{' '}<span className="text-gold">{STATUS_LABELS[getStatus(duplicate)]}</span>
            </p>
          </div>
        )}
      </div>

      {/* Status */}
      <div>
        <label className="text-cream-muted text-xs font-medium block mb-1.5">Status</label>
        <div className="flex gap-2 flex-wrap">
          {statusOptions.map(o => (
            <button key={o.key} type="button" onClick={() => setAddStatus(o.key)}
              className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${
                addStatus === o.key
                  ? 'bg-gold text-ink-base border-gold font-semibold'
                  : 'border-ink-border text-cream-dim hover:border-ink-muted hover:text-cream'
              }`}>
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Progress (reading / abandoned) */}
      {(addStatus === 'reading' || addStatus === 'abandoned') && (
        <div>
          <label className="text-cream-muted text-xs font-medium block mb-1.5">Progress — {progress}%</label>
          <input type="range" min="0" max="100" value={progress}
            onChange={e => setProgress(+e.target.value)}
            className="w-full accent-gold" />
        </div>
      )}

      {/* Dates toggle */}
      {addStatus !== 'wishlist' && (
        <div>
          <button type="button" onClick={() => setShowDates(o => !o)}
            className="text-xs text-cream-muted hover:text-cream transition-colors">
            {showDates ? '− Hide dates' : '+ Add dates'}
          </button>
          {showDates && (
            <div className="flex gap-3 mt-2.5">
              {(addStatus === 'reading' || addStatus === 'finished' || addStatus === 'abandoned') && (
                <div className="flex-1">
                  <label className="text-cream-muted text-[11px] block mb-1">Started</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                    className="sc-input text-xs py-1.5" />
                </div>
              )}
              {addStatus === 'finished' && (
                <div className="flex-1">
                  <label className="text-cream-muted text-[11px] block mb-1">Finished</label>
                  <input type="date" value={finishDate} onChange={e => setFinishDate(e.target.value)}
                    className="sc-input text-xs py-1.5" />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        <button type="submit" disabled={loading}
          className="sc-btn-gold text-xs py-2 px-5 disabled:opacity-50">
          {loading ? 'Adding…' : 'Add book'}
        </button>
        <button type="button" onClick={onCancel} className="sc-btn-ghost text-xs py-2 px-4">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Stat ──────────────────────────────────────────────────────────────────────
function Stat({ value, label, sub, onClick }) {
  return (
    <div className={`flex flex-col gap-0.5 ${onClick ? 'cursor-pointer group' : ''}`} onClick={onClick}>
      <span className="font-serif text-3xl font-semibold text-cream tracking-tight tabular-nums">{value}</span>
      <span className="text-cream-muted text-xs">{label}</span>
      {sub && <span className="text-[10px] text-cream-muted/60">{sub}</span>}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
function AddBook() {
  const [allBooks,        setAllBooks]        = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [recoLoading,     setRecoLoading]     = useState(false);
  const [recoFetched,     setRecoFetched]     = useState(false);
  const [showAddForm,     setShowAddForm]     = useState(false);
  const [selectedBook,    setSelectedBook]    = useState(null);
  const [isChatOpen,      setIsChatOpen]      = useState(false);
  const [noteBook,        setNoteBook]        = useState(null); // eslint-disable-line no-unused-vars
  const [note,            setNote]            = useState("");
  const [showNoteModal,   setShowNoteModal]   = useState(false);
  const [readingGoal,     setReadingGoal]     = useState(() => +localStorage.getItem('readingGoal') || 0);
  const [editingGoal,     setEditingGoal]     = useState(false);
  const [goalInput,       setGoalInput]       = useState('');

  const firstName = localStorage.getItem("firstName") || "Reader";
  const today = new Intl.DateTimeFormat("en", { weekday: "long", month: "long", day: "numeric" }).format(new Date());

  const fetchAllBooks = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;
    try {
      const res = await axios.get(`${LocalApiUrl}/books/${userId}`);
      setAllBooks(normaliseBooksResponse(res.data).sort((a, b) => new Date(b.addedOn) - new Date(a.addedOn)));
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchAllBooks(); }, []); // eslint-disable-line
  useEffect(() => {
    const finished = allBooks.filter(b => getStatus(b) === 'finished');
    if (finished.length > 0 && !recoFetched) getRecommendations(finished, false);
  }, [allBooks]); // eslint-disable-line

  // Derived buckets
  const reading   = allBooks.filter(b => getStatus(b) === 'reading');
  const finished  = allBooks.filter(b => getStatus(b) === 'finished');
  const wishlist  = allBooks.filter(b => getStatus(b) === 'wishlist'); // eslint-disable-line no-unused-vars
  const thisYear  = new Date().getFullYear();
  const doneThisYear = finished.filter(b => {
    const d = b.finishDate || b.addedOn;
    return d && new Date(d).getFullYear() === thisYear;
  }).length;
  const recentFinished = finished.slice(0, 8);
  const featured  = recommendations[0] || null;
  const otherPicks = recommendations.slice(1);

  // API helpers
  const updateBook = async (book, updates) => {
    await axios.post(`/api/books/update`, { id: book._id, ...updates });
    await fetchAllBooks();
  };

  const handleAddBook = async ({ title, status, startDate, finishDate, progress }) => {
    // Get details from AI
    const data = await openaiChat({
      model: GPT_MODEL,
      messages: [{ role: "user", content: `Provide ISBN, title, author, release_year for "${title}". Return JSON: {"ISBN":"...","title":"...","author":"...","release_year":"..."}` }],
      max_completion_tokens: 150, temperature: 0.3,
      response_format: { type: "json_object" },
    });
    let details;
    try { details = parseJsonReply(extractChatMessageText(data)); } catch { details = { title }; }

    // Fetch cover by title + author (more reliable than ISBN)
    details.imageLink = await fetchCover(details.title || title, details.author);

    const userId = localStorage.getItem("userId");
    await axios.post(`${LocalApiUrl}/books/add`, {
      userId,
      book: { ...details, status, progress, startDate: startDate || null, finishDate: finishDate || null, addedOn: Date.now() },
    });
    setShowAddForm(false);
    await fetchAllBooks();
  };

  const handleAddReco = async (book) => {
    const userId = localStorage.getItem("userId");
    await axios.post(`${LocalApiUrl}/books/add`, {
      userId,
      book: { ...book, status: 'finished', addedOn: Date.now() },
    });
    setRecommendations(r => r.filter(b => b.title !== book.title));
    await fetchAllBooks();
  };
  const handleWishlistReco = async (book) => { await AddToWishlist(book); setRecommendations(r => r.filter(b => b.title !== book.title)); };
  const handleSkipReco     = async (book) => { await AddtoExclusion(book); setRecommendations(r => r.filter(b => b.title !== book.title)); };

  const THEMES = [
    "overlooked literary fiction and quiet masterpieces",
    "propulsive, plot-driven page-turners",
    "narrative nonfiction and immersive journalism",
    "historical fiction and world-building from different cultures",
    "science fiction or speculative futures",
    "psychological suspense and unreliable narrators",
    "memoirs and personal essays",
    "international literature — works in translation",
    "cult classics and underground favorites",
    "recent releases from the last three years",
    "backlist gems from the 1980s and 1990s",
    "short story collections or novellas",
  ];

  const getRecommendations = async (finishedBooks, clearSeen = false) => {
    setRecoLoading(true);
    const seenKey = 'seenRecoTitles';
    if (clearSeen) localStorage.removeItem(seenKey);
    const seen = JSON.parse(localStorage.getItem(seenKey) || '[]');

    try {
      const titles   = pickRandom(finishedBooks, 20).map(b => b.title).join(", ");
      const excluded = [
        ...allBooks.filter(b => getStatus(b) === 'abandoned').map(b => b.title),
        ...seen,
      ].join(", ");
      const theme = THEMES[Math.floor(Math.random() * THEMES.length)];
      const data = await openaiChat({
        model: GPT_MODEL,
        messages: [
          { role: "system", content: "You are a world-class book recommendation engine. Favour hidden gems, cult classics, and overlooked works over popular bestsellers. Never repeat a recommendation the user has already seen." },
          { role: "user",   content: `Reading history: ${titles}.\nAlready recommended or disliked (exclude these entirely): ${excluded}.\nToday's focus: ${theme}.\nGive exactly 3 books from 3 different genres. Return JSON {Book1,Book2,Book3} each with: title, author, ISBN, release_year, genre, because_you_read, summary (20 words max).` },
        ],
        max_completion_tokens: 1500, temperature: 1.0,
        response_format: { type: "json_object" },
      });
      const obj = parseJsonReply(extractChatMessageText(data));
      const books = await Promise.all(
        Object.values(obj).map(async b => ({ ...b, imageLink: await fetchCover(b.title, b.author) }))
      );
      setRecommendations(books);
      setRecoFetched(true);
      const newTitles = [...seen, ...books.map(b => b.title)].slice(-30);
      localStorage.setItem(seenKey, JSON.stringify(newTitles));
    } catch (err) { console.error(err); }
    finally { setRecoLoading(false); }
  };

  const handleUpdateProgress = (book, pct) => updateBook(book, { progress: pct });
  const handleMarkFinished   = (book) => updateBook(book, { status: 'finished', progress: 100, finishDate: new Date().toISOString() });
  const handleAbandon        = (book) => updateBook(book, { status: 'abandoned' });

  const saveNote = async () => {
    if (!noteBook) return;
    const { data } = await axios.post(`/api/books/update`, { id: noteBook._id, note });
    if (data.success) setAllBooks(prev => prev.map(b => b._id === noteBook._id ? { ...b, note: data.book.note } : b));
    setShowNoteModal(false);
  };

  const saveGoal = () => {
    const g = +goalInput;
    localStorage.setItem('readingGoal', g);
    setReadingGoal(g);
    setEditingGoal(false);
  };

  return (
    <div className="sc-page">
      <div className="max-w-7xl mx-auto pt-9 sm:pt-14 space-y-12">

        {/* Top bar */}
        <div className="flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <p className="sc-kicker mb-2">{today}</p>
            <h1 className="font-serif text-4xl font-semibold tracking-[-0.035em] text-cream sm:text-5xl">Good to see you, {firstName}.</h1>
            <p className="mt-2 text-sm text-cream-muted">Your reading world, right where you left it.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/library" className="sc-icon-button no-underline" aria-label="Search library"><Icon name="search" size={17} /></Link>
            <button onClick={() => setShowAddForm(o => !o)} className="sc-btn-gold px-4 py-2.5 text-xs">
              <Icon name={showAddForm ? "close" : "plus"} size={15} /> {showAddForm ? 'Close' : 'Add a book'}
            </button>
          </div>
        </div>

        {/* Add form */}
        {showAddForm && (
          <AddForm allBooks={allBooks} onAdd={handleAddBook} onCancel={() => setShowAddForm(false)} />
        )}

        {/* Currently reading */}
        {reading.length > 0 && (
          <section>
            <SectionHeader label="Right now" title="Currently reading" />
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-5 lg:grid-cols-5">
              {reading.map((book, i) => (
                <CurrentlyReadingCard key={book._id || i} book={book}
                  onUpdate={handleUpdateProgress}
                  onFinish={handleMarkFinished}
                  onAbandon={handleAbandon}
                  onChat={b => { setSelectedBook(b); setIsChatOpen(true); }}
                />
              ))}
            </div>
          </section>
        )}

        {/* Stats */}
        {allBooks.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="sc-panel px-5 py-5 sm:px-6">
              <Stat value={finished.length} label="books read" />
            </div>
            <div className="sc-panel px-5 py-5 sm:px-6">
              <Stat value={doneThisYear} label={`in ${thisYear}`} />
            </div>
            <div className="sc-panel px-5 py-5 sm:px-6">
              <Stat value={reading.length} label="reading now" />
            </div>
            <div className="sc-panel px-5 py-5 sm:px-6">
              {editingGoal ? (
                <div className="flex items-center gap-1.5">
                  <input autoFocus type="number" min="0" value={goalInput}
                    onChange={e => setGoalInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && saveGoal()}
                    placeholder="24"
                    className="w-14 bg-ink-raised border border-ink-border text-cream text-sm rounded px-2 py-1 text-center" />
                  <button onClick={saveGoal} className="text-[10px] text-gold hover:text-gold-light transition-colors">Save</button>
                </div>
              ) : readingGoal > 0 ? (
                <div onClick={() => { setGoalInput(readingGoal); setEditingGoal(true); }}>
                  <Stat
                    value={`${doneThisYear}/${readingGoal}`}
                    label="yearly goal"
                    sub={doneThisYear >= readingGoal ? '✓ Achieved' : `${readingGoal - doneThisYear} to go`}
                    onClick={() => {}}
                  />
                </div>
              ) : (
                <button onClick={() => { setGoalInput(''); setEditingGoal(true); }}
                  className="text-left">
                  <span className="text-2xl font-bold text-ink-muted">—</span>
                  <span className="block text-cream-muted text-xs mt-0.5 hover:text-cream transition-colors">
                    Set reading goal
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* AI pick */}
        {(recoLoading || featured) && (
          <section>
            <div className="flex items-end justify-between mb-5">
              <SectionHeader label="Read next" title="Picked for you" inline />
              {recoFetched && !recoLoading && (
                <button onClick={() => { setRecoFetched(false); setRecommendations([]); getRecommendations(finished, true); }}
                  className="inline-flex items-center gap-1.5 text-xs text-cream-muted hover:text-cream transition-colors">
                  <Icon name="sparkle" size={14} /> Refresh picks
                </button>
              )}
            </div>
            <FeaturedPick book={featured} loading={recoLoading}
              onAdd={handleAddReco} onWishlist={handleWishlistReco} onExclude={handleSkipReco} />

            {otherPicks.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                {otherPicks.map((book, i) => (
                  <div key={i} className="flex gap-3 bg-ink-surface/80 border border-ink-border rounded-2xl p-4 hover:border-ink-muted hover:-translate-y-0.5 transition-all duration-200">
                    <div className="w-10 h-14 rounded overflow-hidden flex-shrink-0">
                      <CoverArt book={book} className="w-full h-full" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {book.genre && <p className="text-gold text-[10px] font-semibold uppercase tracking-wider mb-0.5">{book.genre}</p>}
                      <p className="text-cream text-xs font-semibold line-clamp-1">{book.title}</p>
                      <p className="text-cream-muted text-[11px]">{book.author}</p>
                      {book.because_you_read && <p className="text-cream-muted text-[10px] mt-1">Because you read <span className="text-cream-dim">{book.because_you_read}</span></p>}
                      <div className="flex gap-2 mt-2">
                        <button onClick={() => handleAddReco(book)} className="text-[11px] text-cream-dim border border-ink-border px-2 py-0.5 rounded hover:border-gold/40 hover:text-gold transition-colors">Add</button>
                        <button onClick={() => handleWishlistReco(book)} className="text-[11px] text-cream-dim border border-ink-border px-2 py-0.5 rounded hover:border-ink-muted hover:text-cream transition-colors">Save</button>
                        <button onClick={() => handleSkipReco(book)} className="text-[11px] text-cream-muted hover:text-cream transition-colors px-1">Skip</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Recently finished — chat strip */}
        {recentFinished.length > 0 && (
          <section>
            <SectionHeader label="Chat with characters" title="Recently finished" />
            <div className="flex gap-5 overflow-x-auto pb-3 scrollbar-none -mx-1 px-1">
              {recentFinished.map((book, i) => (
                <RecentCard key={book._id || i} book={book}
                  onChat={b => { setSelectedBook(b); setIsChatOpen(true); }} />
              ))}
            </div>
            <p className="text-cream-muted text-[11px] mt-2">Tap a book to talk to its characters</p>
          </section>
        )}

        {/* Empty state */}
        {allBooks.length === 0 && !showAddForm && (
          <div className="sc-panel mx-auto max-w-2xl py-20 px-6 text-center">
            <span className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gold/10 text-gold"><Icon name="library" size={24} /></span>
            <p className="font-serif text-cream text-3xl font-semibold mb-2">Start your library</p>
            <p className="text-cream-muted text-sm mb-8 max-w-xs mx-auto">
              Add a book to track your reading, get personal recommendations, and chat with characters.
            </p>
            <button onClick={() => setShowAddForm(true)} className="sc-btn-gold"><Icon name="plus" size={16} /> Add your first book</button>
          </div>
        )}

        {/* Link to full library */}
        {finished.length > 4 && (
          <div className="text-center pt-4">
            <Link to="/library" className="sc-btn-ghost text-sm px-8 py-2.5">
              Browse all {finished.length} finished books →
            </Link>
          </div>
        )}
      </div>

      <ChatApp isOpen={isChatOpen} onClose={() => { setIsChatOpen(false); setSelectedBook(null); }} selectedBook={selectedBook} />

      <Modal show={showNoteModal} onHide={() => setShowNoteModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-cream text-sm font-semibold">{noteBook?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-cream-muted text-xs mb-3">{noteBook?.author}</p>
          <textarea className="sc-input min-h-[120px] resize-none text-sm"
            placeholder="Your thoughts, quotes, reflections…"
            value={note} onChange={e => setNote(e.target.value)} />
        </Modal.Body>
        <Modal.Footer>
          <button className="sc-btn-ghost text-xs py-1.5 px-4" onClick={() => setShowNoteModal(false)}>Cancel</button>
          <button className="sc-btn-gold text-xs py-1.5 px-4" onClick={saveNote}>Save</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

function SectionHeader({ label, title, inline = false }) {
  return (
    <div className={inline ? "" : "mb-5"}>
      <p className="sc-kicker mb-1">{label}</p>
      <h2 className="font-serif text-2xl font-semibold text-cream sm:text-3xl">{title}</h2>
    </div>
  );
}

export default AddBook;
